import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Button, Tag, Divider, Typography, Alert, Collapse, Modal, message } from 'antd';
import {
  GlobalOutlined,
  LinkedinOutlined,
  MailOutlined,
  ExportOutlined,
  SendOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { Application, ApplicationStatus, LLMRating } from '../../types';
import { retryContact } from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { canGenerateInterviewKit } from '../../utils/interviewKit';
import { renderBulletList } from '../../utils/renderUtils';
import { contactLabel } from '../../utils/contactUtils';
import { isDeclaredApplication } from '../../utils/applicationChannel';
import EmailThread from '../email/EmailThread';
import ComposeEmailModal from '../modals/ComposeEmailModal';
import InterviewKitModal from '../modals/InterviewKitModal';
import ReplyCaptureDrawerHint from '../email/ReplyCaptureDrawerHint';
import ForwardingAddressCard from '../email/ForwardingAddressCard';
import {
  isAppHintDismissed,
  dismissAppHint,
  getGmailAddonUrl,
} from '../../utils/replyCaptureStorage';
import SourceTag from '../common/SourceTag';
import css from './ApplicationDrawer.module.scss';

const { Title, Text, Paragraph } = Typography;

interface ApplicationDrawerProps {
  application: Application | null;
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

const ratingConfig: Record<LLMRating, { label: string; color: string }> = {
  perfect_fit: { label: 'Perfect Fit', color: '#52c41a' },
  strong_alignment: { label: 'Strong Alignment', color: '#73d13d' },
  potential_candidate: { label: 'Potential Candidate', color: '#faad14' },
  partial_fit: { label: 'Partial Fit', color: '#fa8c16' },
  misalignment: { label: 'Misalignment', color: '#ff4d4f' },
};

const statusColors: Record<ApplicationStatus, string> = {
  submitted: 'blue',
  under_review: 'gold',
  screening_call: 'green',
  first_interview: 'green',
  second_interview: 'green',
  additional_interview: 'green',
  technical_assessment: 'green',
  reference_check: 'green',
  offer_extended: 'purple',
  negotiation: 'purple',
  hired: 'gold',
  rejected: 'red',
  withdrawn: 'default',
};

const statusDisplayNames: Record<ApplicationStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  screening_call: 'Screening Call',
  first_interview: 'First Interview',
  second_interview: 'Second Interview',
  additional_interview: 'Additional Interview',
  technical_assessment: 'Technical Assessment',
  reference_check: 'Reference Check',
  offer_extended: 'Offer Extended',
  negotiation: 'Negotiation',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ApplicationDrawer: React.FC<ApplicationDrawerProps> = ({
  application,
  open,
  onClose,
  onRefresh,
}) => {
  const [composeMode, setComposeMode] = useState<'follow_up' | 'reply' | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !application) return;
    setHintDismissed(isAppHintDismissed(application.id));
  }, [open, application]);

  const handleHintSetUp = () => {
    window.open(getGmailAddonUrl(), '_blank', 'noopener');
  };

  const handleHintDismiss = () => {
    if (!application) return;
    dismissAppHint(application.id);
    setHintDismissed(true);
  };

  const handleRetryContact = () => {
    if (!application) return;
    Modal.confirm({
      title: 'Retry with different contact?',
      content: `This will archive your current application at ${application.company.name} and create a new opportunity with a different contact.`,
      okText: 'Retry Contact',
      okButtonProps: { style: { backgroundColor: '#7c3aed', borderColor: '#7c3aed' } },
      onOk: async () => {
        setRetryLoading(true);
        try {
          const result = await retryContact(application.opportunityId);
          message.success(
            `New opportunity created with ${result.newContact.firstName} ${result.newContact.lastName}. Review your new email.`
          );
          onClose();
          onRefresh?.();
          navigate('/opportunities');
        } catch (error) {
          message.error(getApiErrorMessage(error));
        } finally {
          setRetryLoading(false);
        }
      },
    });
  };

  if (!application) return null;

  const { opportunity, company, contact } = application;
  const isDeclared = isDeclaredApplication(application);
  const rating = ratingConfig[opportunity.llmRating];
  const hasEmails = application.emails && application.emails.length > 0;
  const emailCount = application.emails?.length ?? 0;
  const hasInboundReply = (application.emails ?? []).some(e => e.direction === 'inbound');
  // Possessive, for the blocked-email banner below.
  const blockedRecipient = contact
    ? `${`${contact.firstName} ${contact.lastName}`.trim()}'s`
    : "this contact's";
  const showReplyCaptureHint = !isDeclared && !hintDismissed && !hasInboundReply && !!contact;

  const bannerParts = [`Applied on ${formatDate(application.submittedAt)}`];
  if (emailCount > 0) {
    bannerParts.push(`${emailCount} ${emailCount === 1 ? 'email' : 'emails'}`);
  }
  if (application.followedUp) {
    bannerParts.push('Followed up');
  }

  return (
    <>
      <Drawer
        title={null}
        placement="right"
        // Matches OpportunityDrawer — the two drawers should feel like siblings.
        width="min(720px, 94vw)"
        onClose={onClose}
        open={open}
        className={css.applicationDrawer}
        footer={
          <div className={css.footer}>
            <Button onClick={onClose}>Close</Button>
            <div className={css.footerRight}>
              {!isDeclared && application.recommendedAction === 'follow_up_suggested' && (
                <Button
                  type="primary"
                  icon={<ClockCircleOutlined />}
                  onClick={() => setComposeMode('follow_up')}
                  className={css.followUpButton}
                >
                  Follow Up
                </Button>
              )}
              {!isDeclared && application.recommendedAction === 'reply_needed' && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => setComposeMode('reply')}
                  className={css.replyButton}
                >
                  Reply
                </Button>
              )}
              {!isDeclared && application.recommendedAction === 'retry_different_contact' && (
                <Button
                  type="primary"
                  icon={<UserSwitchOutlined />}
                  onClick={handleRetryContact}
                  loading={retryLoading}
                  className={css.retryContactButton}
                >
                  Retry Contact
                </Button>
              )}
              {canGenerateInterviewKit(application.applicationStatus) && (
                <Button
                  type="primary"
                  icon={<SolutionOutlined />}
                  onClick={() => setKitOpen(true)}
                  className={css.interviewKitButton}
                >
                  Interview Kit
                </Button>
              )}
              <Button
                icon={<ExportOutlined />}
                onClick={() => window.open(opportunity.jobPostingUrl, '_blank')}
              >
                View Job Posting
              </Button>
            </div>
          </div>
        }
      >
        {/* Header */}
        <div className={css.header}>
          <div className={css.titleRow}>
            <Title level={4} className={css.companyName}>
              {company.name}
            </Title>
          </div>
          <Text className={css.jobTitle}>{opportunity.title}</Text>
          <div className={css.tags}>
            <SourceTag source={opportunity.source} sourceSite={opportunity.sourceSite} />
            <Tag color={statusColors[application.applicationStatus]}>
              {statusDisplayNames[application.applicationStatus]}
            </Tag>
            <Tag color={rating.color}>{rating.label}</Tag>
            <span className={css.similarity}>
              {Math.round(opportunity.similarityScore * 100)}% match
            </span>
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          className={css.calloutBanner}
          message={bannerParts.join(' \u00b7 ')}
        />

        {showReplyCaptureHint && (
          <ReplyCaptureDrawerHint
            contactFirstName={contact?.firstName ?? ''}
            onSetUp={handleHintSetUp}
            onDismiss={handleHintDismiss}
          />
        )}

        {/* Recommended Action Banner */}
        {!isDeclared && application.recommendedAction === 'reply_needed' && (
          <Alert
            type="warning"
            showIcon
            icon={<SendOutlined />}
            className={css.actionBanner}
            message="Reply needed"
            description={`A ${contact ? contactLabel(contact.contactType) : 'contact'} message is waiting for your response.`}
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => setComposeMode('reply')}
                className={css.replyActionButton}
              >
                Draft Reply
              </Button>
            }
          />
        )}
        {!isDeclared && application.recommendedAction === 'follow_up_suggested' && (
          <Alert
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            className={css.actionBanner}
            message="Follow-up suggested"
            description="It's been 7+ days with no response. Consider following up."
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => setComposeMode('follow_up')}
                className={css.followUpActionButton}
              >
                Draft Follow-Up
              </Button>
            }
          />
        )}
        {!isDeclared && application.recommendedAction === 'retry_different_contact' && (
          <Alert
            type="warning"
            showIcon
            icon={<UserSwitchOutlined />}
            className={css.actionBanner}
            // Two reasons lead here. A follow-up went unanswered, or (D20) a
            // follow-up or reply was refused because the contact's address
            // couldn't be confirmed — "no response" would then blame someone
            // for an email that was never sent.
            message={
              application.recipientRejectedAt
                ? "Your email wasn't sent"
                : 'Retry with different contact'
            }
            description={
              application.recipientRejectedAt
                ? `We couldn't confirm ${blockedRecipient} email address, so we didn't send it. You can try a different contact at this company.`
                : 'No response after follow-up. Try reaching a different contact at this company.'
            }
            action={
              <Button
                size="small"
                type="primary"
                onClick={handleRetryContact}
                loading={retryLoading}
                className={css.retryContactActionButton}
              >
                Retry Contact
              </Button>
            }
          />
        )}

        <Divider />

        {/* Email Thread */}
        {hasEmails && (
          <>
            <section className={css.section}>
              <Title level={5}>Conversation</Title>
              <EmailThread emails={application.emails!} />
            </section>
            <Divider />
          </>
        )}

        {/* Per-application forwarding address — email-only, so hidden for
            declared apps (no Beast thread to forward replies into). */}
        {!isDeclared && application.forwardingAddress && (
          <ForwardingAddressCard address={application.forwardingAddress} />
        )}

        {/* Contact — or, for declared apps, how the user applied. A declared
            app's `contact` is still the CRAWLED hiring manager we never
            emailed; showing it would imply we contacted them. */}
        {isDeclared ? (
          <section className={css.section}>
            <Title level={5}>How you applied</Title>
            <div
              className={
                application.channel === 'referral'
                  ? `${css.channelCard} ${css.channelCardReferral}`
                  : css.channelCard
              }
            >
              <Text strong>
                {application.channel === 'referral' ? 'Through a referral' : 'Applied directly'}
              </Text>
              <Text type="secondary">
                {application.channel === 'referral'
                  ? 'You went through someone you know — no Beast email was sent.'
                  : 'You applied on your own — no Beast email was sent.'}
              </Text>
              {application.channelNote && (
                <Text className={css.channelNote}>{application.channelNote}</Text>
              )}
            </div>
          </section>
        ) : (
          contact && (
            <section className={css.section}>
              <Title level={5}>Contact</Title>
              <div className={css.contactCard}>
                <div className={css.contactInfo}>
                  <Text strong>
                    {contact.firstName} {contact.lastName}
                  </Text>
                  <Text type="secondary">{contact.position}</Text>
                  <div className={css.email}>
                    <MailOutlined />
                    <Text copyable={{ text: contact.email }}>
                      {contact.email}
                    </Text>
                  </div>
                </div>
                {contact.linkedinUrl && (
                  <a
                    href={contact.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.contactLinkedin}
                  >
                    <LinkedinOutlined />
                  </a>
                )}
              </div>
            </section>
          )
        )}

        <Divider />

        {/* Fit Assessment */}
        <section className={css.section}>
          <Title level={5}>Why You're a Good Fit</Title>
          {renderBulletList(opportunity.suitabilityReason, 'No detailed explanation available.')}
        </section>

        <Divider />

        <Collapse
          ghost
          className={css.detailsCollapse}
          items={[
            ...(opportunity.summary
              ? [
                  {
                    key: 'summary',
                    label: 'Job Summary',
                    children: (
                      <section className={css.section}>
                        <Paragraph>{opportunity.summary.generalOverview}</Paragraph>

                        {opportunity.summary.keyResponsibilities && (
                          <div className={css.listSection}>
                            <Text strong>Key Responsibilities:</Text>
                            <ul>
                              {opportunity.summary.keyResponsibilities.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {opportunity.summary.requiredQualifications && (
                          <div className={css.listSection}>
                            <Text strong>Required Qualifications:</Text>
                            <ul>
                              {opportunity.summary.requiredQualifications.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </section>
                    ),
                  },
                ]
              : []),
            {
              key: 'company',
              label: `About ${company.name}`,
              children: (
                <section className={css.section}>
                  <Paragraph>{company.description}</Paragraph>
                  <div className={css.links}>
                    {company.websiteUrl && (
                      <a href={company.websiteUrl} target="_blank" rel="noopener noreferrer">
                        <GlobalOutlined /> Website
                      </a>
                    )}
                    {company.linkedinUrl && (
                      <a href={company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                        <LinkedinOutlined /> LinkedIn
                      </a>
                    )}
                  </div>
                </section>
              ),
            },
          ]}
        />
      </Drawer>

      {composeMode && (
        <ComposeEmailModal
          applicationId={application.id}
          companyName={company.name}
          mode={composeMode}
          open={true}
          onClose={() => setComposeMode(null)}
          onSuccess={() => {
            setComposeMode(null);
            onRefresh?.();
          }}
          onRefresh={onRefresh}
        />
      )}

      <InterviewKitModal
        applicationId={application.id}
        roleTitle={opportunity.title}
        companyName={company.name}
        status={application.applicationStatus}
        open={kitOpen}
        onClose={() => setKitOpen(false)}
      />
    </>
  );
};

export default ApplicationDrawer;
